import { useCallback, useEffect, useRef, useState } from 'react';
import { humanizeApiError } from '@lapo/core';
import type {
  ShipmentView,
  TrackedPackageView,
  DraftView,
  ShipFromAddress,
  ToastInput,
} from '@lapo/ui';
import type { ApiClient } from './auth/apiClient.js';
import type { AuthSession } from './auth/provider.js';
import { API_URL } from './config.js';
import { panelCacheKey, readPanelCache, writePanelCache } from './panelCache.js';

/**
 * Owns the EasyPost shipping module: the ship-from (return) address, the purchased-label list, drafted
 * labels, and the arbitrary package-tracking list — plus every action the Shipments drawer / assistant
 * drive (draft→buy, sync/import, email scan, refresh/archive/edit a label, track/untrack a number, and
 * the ship-from load/save). Behavior-preserving extraction from App.tsx: it paints from the last-known
 * panel cache, loads when the drawer's Shipments panel becomes visible, and silently backfills new
 * labels on a poll once EasyPost is connected.
 *
 * Deps are passed in (never reached out of App): `api`, the auth `session` (for uid + gating), the
 * connected `easypost` account (gates the background poll), `pushToast`, and `shipmentsPanelVisible`
 * (App computes `rightOpen && RIGHT_VIEWS[rightViewIndex]?.id === 'shipping'` and passes it, reproducing
 * the inline effect's exact gate). `session` null (signed out) keeps everything empty/local.
 */
const SHIPMENT_POLL_MS = 5 * 60 * 1000;

export interface UseShipmentsDeps {
  api: ApiClient;
  session: AuthSession | null;
  easypost: { accountId: string } | undefined;
  pushToast: (t: ToastInput) => number;
  /** True when the Shipments panel is the visible view in the open right drawer. */
  shipmentsPanelVisible: boolean;
  /** Called after a label is successfully purchased so App can close the Shipments drawer (App-owned
   *  right-drawer UI state — kept a callback so the hook doesn't reach into App's layout). */
  onLabelBought: () => void;
}

export interface UseShipments {
  // Ship-from (return) address.
  shipFromAddress: ShipFromAddress | null;
  shipFromLoading: boolean;
  shipFromSaving: boolean;
  shipFromError: string | undefined;
  onSaveShipFrom: (next: ShipFromAddress) => Promise<void>;
  // Purchased-label list + draft.
  shipments: ShipmentView[];
  shipDraft: DraftView | null;
  setShipDraft: (draft: DraftView | null) => void;
  shipmentsLoading: boolean;
  shipBuying: boolean;
  shipSyncing: boolean;
  scanningEmail: boolean;
  shipRefreshingId: string | undefined;
  shipError: string | undefined;
  // Arbitrary package tracking.
  tracked: TrackedPackageView[];
  trackingBusy: boolean;
  refreshingTrackedId: string | undefined;
  // Loaders.
  loadShipments: () => Promise<void>;
  loadTracked: () => Promise<void>;
  // Actions.
  onSyncShipments: () => Promise<void>;
  onScanEmail: () => Promise<void>;
  onBuyLabel: (easypostId: string, rateId: string) => Promise<void>;
  onRefreshShipment: (shipmentId: string) => Promise<void>;
  onArchiveShipment: (shipmentId: string, archived: boolean) => Promise<void>;
  onEditShipment: (shipmentId: string, patch: { label?: string; note?: string }) => Promise<void>;
  onTrackPackage: (trackingCode: string, carrier?: string) => Promise<void>;
  onRefreshTracked: (trackedId: string) => Promise<void>;
  onUntrack: (trackedId: string) => Promise<void>;
  onArchiveTracked: (trackedId: string, archived: boolean) => Promise<void>;
  onEditTracked: (trackedId: string, patch: { label?: string; item?: string }) => Promise<void>;
}

export function useShipments({
  api,
  session,
  easypost,
  pushToast,
  shipmentsPanelVisible,
  onLabelBought,
}: UseShipmentsDeps): UseShipments {
  // Ship-from (return) address (server-side, per user). Loaded with the dialog; Save persists via the
  // API. This is the sender the ✦ assistant uses when a shipping request names only the recipient.
  const [shipFromAddress, setShipFromAddress] = useState<ShipFromAddress | null>(null);
  const [shipFromLoading, setShipFromLoading] = useState(false);
  const [shipFromSaving, setShipFromSaving] = useState(false);
  const [shipFromError, setShipFromError] = useState<string | undefined>(undefined);

  // EasyPost shipping module (server-side). The list of previous labels loads when the Shipments view
  // opens; "Draft with AI" parses a prompt into a priced draft (no charge), and "Buy label" purchases
  // the selected rate (a charge) then prepends the new label to the list.
  const [shipments, setShipments] = useState<ShipmentView[]>([]);
  const [shipDraft, setShipDraft] = useState<DraftView | null>(null);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [shipBuying, setShipBuying] = useState(false);
  const [shipSyncing, setShipSyncing] = useState(false);
  const [scanningEmail, setScanningEmail] = useState(false);
  const [shipRefreshingId, setShipRefreshingId] = useState<string | undefined>(undefined);
  const [shipError, setShipError] = useState<string | undefined>(undefined);
  // Arbitrary package tracking (paste a tracking number). Separate from the bought-label list above.
  const [tracked, setTracked] = useState<TrackedPackageView[]>([]);
  const [trackingBusy, setTrackingBusy] = useState(false);
  const [refreshingTrackedId, setRefreshingTrackedId] = useState<string | undefined>(undefined);

  // Track the live session in a ref so the async loaders can gate on the current identity without
  // re-creating on every session change (mirrors App's sessionRef guard).
  const sessionRef = useRef<AuthSession | null>(session);
  sessionRef.current = session;

  // Load the ship-from (return) address once signed in — the EasyPost integration's detail page (in
  // the Integrations hub) shows the address form, so it can't wait for the Settings dialog to open.
  useEffect(() => {
    if (!session || !API_URL) return;
    setShipFromLoading(true);
    setShipFromError(undefined);
    void (async () => {
      try {
        setShipFromAddress(await api.getShipFrom());
      } catch (err) {
        setShipFromError(`Couldn't load ship-from address: ${humanizeApiError(err)}`);
      } finally {
        setShipFromLoading(false);
      }
    })();
  }, [session, api]);

  const onSaveShipFrom = useCallback(
    async (next: ShipFromAddress) => {
      setShipFromSaving(true);
      setShipFromError(undefined);
      try {
        setShipFromAddress(await api.putShipFrom(next));
        pushToast({ tone: 'success', message: 'Ship-from address saved.' });
      } catch (err) {
        setShipFromError(humanizeApiError(err));
        pushToast({ tone: 'error', title: 'Could not save', message: humanizeApiError(err) });
      } finally {
        setShipFromSaving(false);
      }
    },
    [api, pushToast],
  );

  const loadShipments = useCallback(async () => {
    if (!API_URL || !sessionRef.current) return;
    setShipmentsLoading(true);
    setShipError(undefined);
    try {
      setShipments(await api.listShipments());
    } catch (err) {
      setShipError(`Couldn't load shipments: ${humanizeApiError(err)}`);
    } finally {
      setShipmentsLoading(false);
    }
  }, [api]);

  const loadTracked = useCallback(async () => {
    if (!API_URL || !sessionRef.current) return;
    try {
      setTracked(await api.listTracked());
    } catch {
      /* best-effort; the track action surfaces errors */
    }
  }, [api]);

  // Hydrate the labels + tracked lists from the last-known cache so the panel paints instantly; the
  // loads below then refresh them in the background. Runs once when the signed-in identity is known.
  useEffect(() => {
    if (!session) return;
    const cached = readPanelCache<ShipmentView[]>(panelCacheKey('shipments', session.user.uid));
    if (cached && cached.length) setShipments(cached);
    const cachedTracked = readPanelCache<TrackedPackageView[]>(panelCacheKey('tracked', session.user.uid));
    if (cachedTracked && cachedTracked.length) setTracked(cachedTracked);
  }, [session]);

  // Persist the lists for the next hydrate. Skip empty writes so the initial [] can't clobber the cache
  // before hydration runs.
  useEffect(() => {
    if (session && shipments.length) writePanelCache(panelCacheKey('shipments', session.user.uid), shipments);
  }, [session, shipments]);
  useEffect(() => {
    if (session && tracked.length) writePanelCache(panelCacheKey('tracked', session.user.uid), tracked);
  }, [session, tracked]);

  // Load the labels + tracked lists when the Shipments panel becomes visible in the right drawer.
  useEffect(() => {
    if (shipmentsPanelVisible && session) {
      void loadShipments();
      void loadTracked();
    }
  }, [shipmentsPanelVisible, session, loadShipments, loadTracked]);

  // Pull the latest purchased labels from EasyPost (deduped) AND refresh every stored label's delivery
  // status in one shot — the manual, feedback-giving version of the silent background poll below.
  const onSyncShipments = useCallback(async () => {
    setShipSyncing(true);
    setShipError(undefined);
    try {
      const { imported, refreshed, shipments: all } = await api.syncShipments(20);
      setShipments(all);
      pushToast({
        tone: 'success',
        message: imported
          ? `Imported ${imported} new label${imported === 1 ? '' : 's'} and refreshed ${refreshed} status${refreshed === 1 ? '' : 'es'}.`
          : `Refreshed ${refreshed} label status${refreshed === 1 ? '' : 'es'}.`,
      });
    } catch (err) {
      setShipError(humanizeApiError(err));
      pushToast({ tone: 'error', title: 'Could not refresh labels', message: humanizeApiError(err) });
    } finally {
      setShipSyncing(false);
    }
  }, [api, pushToast]);

  // Scan every linked Gmail mailbox for shipment mail the INBOX-only watch never delivered (e.g. shipping
  // notifications a filter auto-archives). Reloads the tracked list so any new packages appear.
  const onScanEmail = useCallback(async () => {
    setScanningEmail(true);
    setShipError(undefined);
    try {
      const { mailboxes, tracked: added } = await api.scanEmailForShipments();
      if (added > 0) setTracked(await api.listTracked());
      pushToast({
        tone: 'success',
        message: added
          ? `Found ${added} new package${added === 1 ? '' : 's'} across ${mailboxes} mailbox${mailboxes === 1 ? '' : 'es'}.`
          : `No new shipments found across ${mailboxes} mailbox${mailboxes === 1 ? '' : 'es'}.`,
      });
    } catch (err) {
      setShipError(humanizeApiError(err));
      pushToast({ tone: 'error', title: 'Could not scan email', message: humanizeApiError(err) });
    } finally {
      setScanningEmail(false);
    }
  }, [api, pushToast]);

  // Quietly keep the labels list current: shipments are often created outside Lapo, so once EasyPost
  // is connected, poll for new ones on an interval (and once on connect). Silent — no toasts/errors;
  // the manual "Import recent" button stays the visible, feedback-giving path.
  useEffect(() => {
    if (!API_URL || !session || !easypost) return;
    let cancelled = false;
    const run = async () => {
      try {
        const { shipments: all } = await api.backfillShipments(10);
        if (!cancelled) setShipments(all);
      } catch {
        /* background best-effort; the manual button surfaces errors */
      }
    };
    void run();
    const id = setInterval(() => void run(), SHIPMENT_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [session, easypost, api]);

  const onBuyLabel = useCallback(
    async (easypostId: string, rateId: string) => {
      setShipBuying(true);
      setShipError(undefined);
      try {
        const shipment = await api.buyLabel(easypostId, rateId);
        setShipments((prev) => [shipment, ...prev]);
        setShipDraft(null); // drop the draft/rate table — the purchase is done
        onLabelBought(); // and step out of the way: close the Shipments drawer (App-owned)
        pushToast({
          tone: 'success',
          message: `Label purchased — ${shipment.carrier} ${shipment.service}.`,
          // Link straight to the printable label, so buying → printing is one click.
          ...(shipment.labelUrl ? { action: { label: 'Print label', href: shipment.labelUrl } } : {}),
        });
      } catch (err) {
        setShipError(humanizeApiError(err));
        pushToast({ tone: 'error', title: 'Could not buy label', message: humanizeApiError(err) });
      } finally {
        setShipBuying(false);
      }
    },
    [api, pushToast, onLabelBought],
  );

  const onRefreshShipment = useCallback(
    async (shipmentId: string) => {
      setShipRefreshingId(shipmentId);
      try {
        const updated = await api.refreshShipment(shipmentId);
        setShipments((prev) => prev.map((s) => (s.shipmentId === shipmentId ? updated : s)));
      } catch (err) {
        pushToast({ tone: 'error', title: 'Could not refresh', message: humanizeApiError(err) });
      } finally {
        setShipRefreshingId(undefined);
      }
    },
    [api, pushToast],
  );

  const onArchiveShipment = useCallback(
    async (shipmentId: string, archived: boolean) => {
      // Optimistic flag flip; restore on failure.
      const prev = shipments;
      setShipments((cur) => cur.map((s) => (s.shipmentId === shipmentId ? { ...s, archivedAt: archived ? Date.now() : undefined } : s)));
      try {
        const updated = await api.archiveShipment(shipmentId, archived);
        setShipments((cur) => cur.map((s) => (s.shipmentId === shipmentId ? updated : s)));
      } catch (err) {
        setShipments(prev);
        pushToast({ tone: 'error', title: archived ? 'Could not archive' : 'Could not restore', message: humanizeApiError(err) });
      }
    },
    [api, pushToast, shipments],
  );

  const onEditShipment = useCallback(
    async (shipmentId: string, patch: { label?: string; note?: string }) => {
      // Optimistic apply so the edited title/description shows immediately; restore on failure.
      const prev = shipments;
      setShipments((cur) =>
        cur.map((s) =>
          s.shipmentId === shipmentId
            ? {
                ...s,
                ...(patch.label !== undefined ? { label: patch.label.trim() || undefined } : {}),
                ...(patch.note !== undefined ? { note: patch.note.trim() || undefined } : {}),
              }
            : s,
        ),
      );
      try {
        const updated = await api.updateShipmentDetails(shipmentId, patch);
        setShipments((cur) => cur.map((s) => (s.shipmentId === shipmentId ? updated : s)));
      } catch (err) {
        setShipments(prev);
        pushToast({ tone: 'error', title: 'Could not save', message: humanizeApiError(err) });
      }
    },
    [api, pushToast, shipments],
  );

  const onTrackPackage = useCallback(
    async (trackingCode: string, carrier?: string) => {
      setTrackingBusy(true);
      setShipError(undefined);
      try {
        const pkg = await api.trackPackage(trackingCode, carrier);
        setTracked((prev) => [pkg, ...prev]);
        pushToast({ tone: 'success', message: `Tracking ${pkg.carrier || 'package'} #${pkg.trackingCode}.` });
      } catch (err) {
        setShipError(humanizeApiError(err));
        pushToast({ tone: 'error', title: 'Could not track package', message: humanizeApiError(err) });
      } finally {
        setTrackingBusy(false);
      }
    },
    [api, pushToast],
  );

  const onRefreshTracked = useCallback(
    async (trackedId: string) => {
      setRefreshingTrackedId(trackedId);
      try {
        const updated = await api.refreshTracked(trackedId);
        setTracked((prev) => prev.map((p) => (p.trackedId === trackedId ? updated : p)));
      } catch (err) {
        pushToast({ tone: 'error', title: 'Could not refresh', message: humanizeApiError(err) });
      } finally {
        setRefreshingTrackedId(undefined);
      }
    },
    [api, pushToast],
  );

  const onUntrack = useCallback(
    async (trackedId: string) => {
      // Optimistic remove; restore on failure so a transient error doesn't lose the row.
      const prev = tracked;
      setTracked((cur) => cur.filter((p) => p.trackedId !== trackedId));
      try {
        await api.untrack(trackedId);
      } catch (err) {
        setTracked(prev);
        pushToast({ tone: 'error', title: 'Could not remove', message: humanizeApiError(err) });
      }
    },
    [api, pushToast, tracked],
  );

  const onArchiveTracked = useCallback(
    async (trackedId: string, archived: boolean) => {
      // Optimistic flag flip; restore on failure.
      const prev = tracked;
      setTracked((cur) => cur.map((p) => (p.trackedId === trackedId ? { ...p, archivedAt: archived ? Date.now() : undefined } : p)));
      try {
        const updated = await api.archiveTracked(trackedId, archived);
        setTracked((cur) => cur.map((p) => (p.trackedId === trackedId ? updated : p)));
      } catch (err) {
        setTracked(prev);
        pushToast({ tone: 'error', title: archived ? 'Could not archive' : 'Could not restore', message: humanizeApiError(err) });
      }
    },
    [api, pushToast, tracked],
  );

  const onEditTracked = useCallback(
    async (trackedId: string, patch: { label?: string; item?: string }) => {
      // Optimistic apply so the edited title/description shows immediately; restore on failure.
      const prev = tracked;
      setTracked((cur) =>
        cur.map((p) =>
          p.trackedId === trackedId
            ? {
                ...p,
                ...(patch.label !== undefined ? { label: patch.label.trim() || undefined } : {}),
                ...(patch.item !== undefined ? { item: patch.item.trim() || undefined } : {}),
              }
            : p,
        ),
      );
      try {
        const updated = await api.updateTrackedDetails(trackedId, patch);
        setTracked((cur) => cur.map((p) => (p.trackedId === trackedId ? updated : p)));
      } catch (err) {
        setTracked(prev);
        pushToast({ tone: 'error', title: 'Could not save', message: humanizeApiError(err) });
      }
    },
    [api, pushToast, tracked],
  );

  return {
    shipFromAddress,
    shipFromLoading,
    shipFromSaving,
    shipFromError,
    onSaveShipFrom,
    shipments,
    shipDraft,
    setShipDraft,
    shipmentsLoading,
    shipBuying,
    shipSyncing,
    scanningEmail,
    shipRefreshingId,
    shipError,
    tracked,
    trackingBusy,
    refreshingTrackedId,
    loadShipments,
    loadTracked,
    onSyncShipments,
    onScanEmail,
    onBuyLabel,
    onRefreshShipment,
    onArchiveShipment,
    onEditShipment,
    onTrackPackage,
    onRefreshTracked,
    onUntrack,
    onArchiveTracked,
    onEditTracked,
  };
}
